import { Link } from 'react-router-dom';

interface RelatedCase {
  id: string;
  title: string;
  subtitle: string;
  tag: string;
  img: string;
}

interface ServiceCategory {
  id: string;
  tag: string;
  tagLabel: string;
  title: string;
  subtitle: string;
  intro: string;
  img: string;
  items: { icon: string; title: string; desc: string }[];
  relatedCases: RelatedCase[];
}

const serviceCategories: ServiceCategory[] = [
  {
    id: 'strategy',
    tag: 'A',
    tagLabel: '策略規劃',
    title: '商業空間策略規劃',
    subtitle: 'Space Strategy',
    intro: '在動筆設計之前，我們先幫你想清楚。從品牌定位、目標客群到空間機能，以商業思維為基礎，制定真正有效的空間策略。',
    img: 'https://readdy.ai/api/search-image?query=professional%20architects%20discussing%20commercial%20space%20planning%20floor%20plan%20layout%20strategy%20meeting%20modern%20office%20warm%20atmosphere%20design%20studio&width=900&height=600&seq=svc001&orientation=landscape',
    items: [
      { icon: 'ri-pie-chart-2-line', title: '品牌定位與空間風格建議', desc: '釐清品牌DNA，提出與品牌調性高度吻合的空間風格方向。' },
      { icon: 'ri-route-line', title: '消費動線規劃', desc: '根據消費者行為研究，設計最優化的進店、瀏覽、購買動線。' },
      { icon: 'ri-layout-grid-line', title: '坪效與機能配置', desc: '分析空間坪效，找出最大化使用效率的機能配置方案。' },
      { icon: 'ri-map-2-line', title: '開店前空間方向建議', desc: '選址評估、空間可行性分析、前期規劃建議，幫你開對店。' },
    ],
    relatedCases: [
      {
        id: 'rihe-restaurant',
        title: '日禾和食',
        subtitle: '品牌禪意日式餐廳空間設計',
        tag: '餐飲空間',
        img: 'https://readdy.ai/api/search-image?query=elegant%20japanese%20zen%20restaurant%20interior%20design%20warm%20wood%20stone%20floor%20soft%20ambient%20lighting%20peaceful%20dining%20atmosphere%20commercial%20space%20neutral%20tones&width=600&height=400&seq=svc_case_s1&orientation=landscape',
      },
      {
        id: 'zhixuan-retail',
        title: '質選良品',
        subtitle: '生活選物品牌旗艦店規劃設計',
        tag: '零售門市',
        img: 'https://readdy.ai/api/search-image?query=modern%20lifestyle%20boutique%20retail%20store%20interior%20design%20wooden%20display%20shelves%20soft%20spotlight%20commercial%20minimalist%20space%20premium%20goods%20elegant&width=600&height=400&seq=svc_case_s2&orientation=landscape',
      },
    ],
  },
  {
    id: 'design',
    tag: 'B',
    tagLabel: '空間設計',
    title: '商業空間設計',
    subtitle: 'Space Design',
    intro: '針對各類商業空間，提供完整設計服務。從概念發想到施工圖出圖，確保設計品質與品牌精神精準落地。',
    img: 'https://readdy.ai/api/search-image?query=stunning%20commercial%20interior%20design%20portfolio%20multiple%20spaces%20restaurant%20retail%20office%20elegant%20modern%20minimalist%20high-end%20warm%20neutral%20tones%20professional%20photography&width=900&height=600&seq=svc002&orientation=landscape',
    items: [
      { icon: 'ri-restaurant-2-line', title: '餐飲空間設計', desc: '咖啡廳、餐廳、飲品品牌等，以氛圍、動線與品牌感為核心。' },
      { icon: 'ri-store-2-line', title: '零售門市設計', desc: '品牌旗艦店、連鎖門市、選物空間，強化品牌識別與購物體驗。' },
      { icon: 'ri-building-4-line', title: '辦公空間設計', desc: '品牌總部、共創空間，打造兼具效率與品牌溫度的工作環境。' },
      { icon: 'ri-heart-pulse-line', title: '診所與醫美空間設計', desc: '以信任感、潔淨感與高端氛圍為設計核心，建立品牌公信力。' },
      { icon: 'ri-layout-masonry-line', title: '展示與接待空間設計', desc: '品牌展廳、接待會所、快閃店，呈現品牌最精華的一面。' },
    ],
    relatedCases: [
      {
        id: 'lumia-clinic',
        title: 'Lumia 醫美診所',
        subtitle: '高端品牌診所空間規劃設計',
        tag: '診所醫美',
        img: 'https://readdy.ai/api/search-image?query=luxury%20medical%20aesthetic%20clinic%20interior%20design%20marble%20reception%20desk%20soft%20warm%20lighting%20clean%20white%20walls%20premium%20healthcare%20space%20sophisticated&width=600&height=400&seq=svc_case_d1&orientation=landscape',
      },
      {
        id: 'horizon-showroom',
        title: 'Horizon 品牌展廳',
        subtitle: '高端建材品牌旗艦展示空間',
        tag: '展示接待',
        img: 'https://readdy.ai/api/search-image?query=luxury%20brand%20showroom%20exhibition%20space%20interior%20design%20materials%20display%20architectural%20lighting%20dramatic%20open%20space%20sophisticated%20gallery%20style&width=600&height=400&seq=svc_case_d2&orientation=landscape',
      },
      {
        id: 'lekafe-chain',
        title: 'Le Kafé 精品咖啡',
        subtitle: '連鎖精品咖啡品牌旗艦店',
        tag: '餐飲空間',
        img: 'https://readdy.ai/api/search-image?query=premium%20specialty%20coffee%20shop%20interior%20design%20warm%20wood%20counter%20pendant%20lights%20cozy%20atmosphere%20brand%20cafe%20minimalist%20elegant%20commercial&width=600&height=400&seq=svc_case_d3&orientation=landscape',
      },
    ],
  },
  {
    id: 'construction',
    tag: 'C',
    tagLabel: '工程整合',
    title: '商業空間裝修工程整合',
    subtitle: 'Construction Integration',
    intro: '設計與工程緊密整合，統一管理所有施工環節，讓業主不需要自己協調多個廠商，一個窗口搞定所有事。',
    img: 'https://readdy.ai/api/search-image?query=commercial%20construction%20site%20interior%20fit-out%20professional%20team%20project%20management%20quality%20control%20modern%20building%20renovation%20clean%20organized&width=900&height=600&seq=svc003&orientation=landscape',
    items: [
      { icon: 'ri-tools-line', title: '工程整合管理', desc: '統籌協調各工班，確保施工品質與設計意圖一致執行。' },
      { icon: 'ri-checkbox-multiple-line', title: '材料建議與採購協助', desc: '依預算與設計需求，提供最適材料選用建議，避免踩雷。' },
      { icon: 'ri-money-dollar-circle-line', title: '預算配置建議', desc: '合理分配裝修預算，讓每一分錢都花在刀口上。' },
      { icon: 'ri-time-line', title: '工期控管', desc: '嚴格執行工期計畫，定期回報進度，確保如期完工。' },
      { icon: 'ri-check-double-line', title: '現場執行與驗收', desc: '全程現場監工，完工後逐項驗收，確保交付品質達標。' },
    ],
    relatedCases: [
      {
        id: 'nova-office',
        title: 'NOVA 創意工作室',
        subtitle: '品牌創意公司辦公空間設計',
        tag: '辦公空間',
        img: 'https://readdy.ai/api/search-image?query=modern%20creative%20studio%20office%20interior%20design%20open%20workspace%20natural%20light%20wood%20furniture%20collaborative%20zone%20brand%20agency%20space%20warm&width=600&height=400&seq=svc_case_c1&orientation=landscape',
      },
      {
        id: 'greenform-office',
        title: 'GreenForm 永續辦公室',
        subtitle: '綠色永續企業品牌辦公空間',
        tag: '辦公空間',
        img: 'https://readdy.ai/api/search-image?query=sustainable%20biophilic%20office%20interior%20design%20living%20green%20wall%20plants%20natural%20wood%20furniture%20warm%20soft%20lighting%20open%20workspace%20eco%20friendly&width=600&height=400&seq=svc_case_c2&orientation=landscape',
      },
    ],
  },
  {
    id: 'renewal',
    tag: 'D',
    tagLabel: '品牌升級',
    title: '品牌升級 / 舊店翻新',
    subtitle: 'Brand Renewal',
    intro: '老店換新裝，品牌重煥生機。我們協助已有基礎的品牌，以最有效率的方式完成空間升級，帶來實質商業效益。',
    img: 'https://readdy.ai/api/search-image?query=commercial%20space%20renovation%20before%20after%20transformation%20brand%20upgrade%20retail%20restaurant%20modern%20elegant%20refresh%20clean%20bright%20high-end%20result&width=900&height=600&seq=svc004&orientation=landscape',
    items: [
      { icon: 'ri-refresh-line', title: '老店翻新規劃', desc: '重新評估空間潛力，以全新設計語彙讓老品牌重獲市場競爭力。' },
      { icon: 'ri-award-line', title: '空間形象升級', desc: '強化視覺識別、材料質感與整體氛圍，提升品牌第一印象。' },
      { icon: 'ri-scissors-cut-line', title: '局部改裝優化', desc: '針對特定問題點進行精準改裝，以最低成本帶來最大效益。' },
      { icon: 'ri-magic-line', title: '品牌轉型空間改造', desc: '品牌重新定位後的空間全面更新，確保空間與新品牌策略一致。' },
    ],
    relatedCases: [
      {
        id: 'muse-hotel-lobby',
        title: 'Muse 精品旅宿',
        subtitle: '設計型精品旅館大廳接待空間',
        tag: '展示接待',
        img: 'https://readdy.ai/api/search-image?query=boutique%20hotel%20lobby%20reception%20interior%20design%20elegant%20warm%20lighting%20travertine%20stone%20floor%20custom%20front%20desk%20artistic%20sculpture%20neutral%20cream%20tones%20luxury&width=600&height=400&seq=svc_case_r1&orientation=landscape',
      },
      {
        id: 'rihe-restaurant',
        title: '日禾和食',
        subtitle: '品牌禪意日式餐廳空間設計',
        tag: '餐飲空間',
        img: 'https://readdy.ai/api/search-image?query=elegant%20japanese%20restaurant%20interior%20renovation%20brand%20upgrade%20zen%20minimalist%20warm%20wood%20stone%20floor%20soft%20ambient%20lighting%20dining%20atmosphere%20transformation&width=600&height=400&seq=svc_case_r2&orientation=landscape',
      },
    ],
  },
];

export default function ServiceDetail() {
  return (
    <section className="py-12">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        {serviceCategories.map((cat, ci) => (
          <div
            key={cat.id}
            id={cat.id}
            className={`py-20 md:py-28 ${ci < serviceCategories.length - 1 ? 'border-b border-[#e2ddd8]' : ''}`}
          >
            <div className={`flex flex-col ${ci % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-14 lg:gap-20`}>
              {/* Image */}
              <div className="lg:w-5/12">
                <div className="relative">
                  <div className="w-full h-[380px] rounded-2xl overflow-hidden">
                    <img
                      src={cat.img}
                      alt={cat.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  {/* Tag */}
                  <div className="absolute -top-5 -left-5 w-14 h-14 flex items-center justify-center bg-[#b8956a] rounded-2xl">
                    <span className="text-white font-bold text-lg">{cat.tag}</span>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="lg:w-7/12 flex flex-col">
                <div className="flex items-center gap-3 mb-5">
                  <span className="text-[#b8956a] text-sm font-medium tracking-[0.2em] uppercase">{cat.tagLabel}</span>
                  <div className="flex-1 h-px bg-[#e2ddd8]"></div>
                </div>

                <h2
                  className="font-bold text-[#1a1a1a] leading-[1.35] mb-2"
                  style={{ fontSize: 'clamp(1.5rem, 3vw, 2.125rem)' }}
                >
                  {cat.title}
                </h2>
                <p
                  className="font-light italic text-[#8a8a8a] mb-5"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.25rem' }}
                >
                  {cat.subtitle}
                </p>
                <p className="text-[#5a5a5a] leading-relaxed mb-8" style={{ fontSize: '1.1875rem' }}>
                  {cat.intro}
                </p>

                {/* Service Items */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
                  {cat.items.map((item, ii) => (
                    <div key={ii} className="flex items-start gap-4 p-5 bg-[#f4f2ef] rounded-xl">
                      <div className="w-9 h-9 flex items-center justify-center bg-[#b8956a]/15 rounded-lg flex-shrink-0">
                        <i className={`${item.icon} text-[#b8956a] text-base`}></i>
                      </div>
                      <div>
                        <p className="font-medium text-[#1a1a1a] text-sm mb-1">{item.title}</p>
                        <p className="text-[#8a8a8a] text-xs leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Related Cases */}
                <div className="mt-auto">
                  <div className="flex items-center gap-3 mb-4">
                    <p className="text-xs font-medium text-[#8a8a8a] uppercase tracking-widest">Related Cases</p>
                    <div className="flex-1 h-px bg-[#e2ddd8]"></div>
                    <Link
                      to="/cases"
                      className="text-xs text-[#b8956a] font-medium hover:underline whitespace-nowrap flex items-center gap-1 cursor-pointer"
                    >
                      查看全部案例
                      <i className="ri-arrow-right-line text-xs"></i>
                    </Link>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    {cat.relatedCases.map((rc) => (
                      <Link
                        key={rc.id}
                        to={`/cases/${rc.id}`}
                        className="group relative overflow-hidden rounded-xl cursor-pointer block"
                      >
                        <div className="w-full h-[140px]">
                          <img
                            src={rc.img}
                            alt={rc.title}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent"></div>

                        {/* Tag */}
                        <div className="absolute top-2.5 left-2.5">
                          <span className="px-2 py-0.5 bg-[#b8956a] text-white text-[10px] font-medium rounded-full">
                            {rc.tag}
                          </span>
                        </div>

                        {/* Hover arrow */}
                        <div className="absolute top-2.5 right-2.5 w-6 h-6 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <i className="ri-arrow-right-up-line text-white text-xs"></i>
                        </div>

                        {/* Info */}
                        <div className="absolute bottom-0 left-0 right-0 p-3">
                          <p className="text-white font-semibold text-xs leading-[1.35]">{rc.title}</p>
                          <p className="text-white/55 text-[10px] mt-0.5 line-clamp-1">{rc.subtitle}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* CTA */}
        <div className="py-16 text-center">
          <p className="text-[#5a5a5a] mb-6" style={{ fontSize: '1.1875rem' }}>
            不確定你的專案適合哪個服務範疇？歡迎免費諮詢。
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-10 py-4 bg-[#b8956a] text-white font-semibold rounded-full whitespace-nowrap hover:bg-[#a07d58] transition-colors duration-300 cursor-pointer text-[1.0625rem]"
          >
            <i className="ri-calendar-check-line"></i>
            預約免費諮詢
          </Link>
        </div>
      </div>
    </section>
  );
}
